"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b ||= {})
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { randomString } from "../../../utils";
import { Label } from "../Label";
import { Text } from "../Text";
import Tooltip from "../Tooltip/Tooltip";
import classNames from "classnames";
import { Expandable } from "../../behaviors/Expandable";
import { duration } from "../../../tokens/tokens-css";
import { IconSize, Warning } from "../../icons";
import { Separator } from "../Separator/Separator";
import * as styles from "./SegmentedControl-css";
const SegmentedControlContext = createContext({});
const labelSize = /* @__PURE__ */ new Map([
  ["xs", "s"],
  ["s", "m"],
  ["m", "m"],
  ["l", "m"]
]);
export const SegmentedControl = (_a) => {
  var _b = _a, {
    children,
    value,
    onChange,
    label,
    labelHidden,
    size = "m",
    error,
    className,
    asymmetric = false
  } = _b, props = __objRest(_b, [
    "children",
    "value",
    "onChange",
    "label",
    "labelHidden",
    "size",
    "error",
    "className",
    "asymmetric"
  ]);
  const [indicator, setIndicator] = useState();
  const [transitioning, setTransitioning] = useState(false);
  const id = useRef(`segmented-${randomString()}`);
  const labelId = `${id.current}-label`;
  const errorId = `${id.current}-error`;
  const optionRefs = useRef([]);
  const indicatorRef = useRef(null);
  const optionsRef = useRef(null);
  const currentOption = useRef();
  const handleKeyDown = (event) => {
    var _a2, _b2;
    const options = optionRefs.current.filter((option) => {
      var _a3;
      return !((_a3 = option.element.current) == null ? void 0 : _a3.disabled);
    });
    const currentIndex = options.indexOf(options.find((option) => option.value === value) || options[0]);
    const { length } = options;
    const prevIndex = (currentIndex - 1 + length) % length;
    const nextIndex = (currentIndex + 1) % length;
    const prevOption = options[prevIndex];
    const nextOption = options[nextIndex];
    if (["ArrowLeft", "ArrowUp"].includes(event.key)) {
      onChange(prevOption.value);
      (_a2 = prevOption.element.current) == null ? void 0 : _a2.focus();
    } else if (["ArrowRight", "ArrowDown"].includes(event.key)) {
      onChange(nextOption.value);
      (_b2 = nextOption.element.current) == null ? void 0 : _b2.focus();
    }
  };
  const registerOption = useCallback(
    (optionRef, optionValue) => {
      if (optionValue === value) {
        currentOption.current = optionRef.current;
      }
      optionRefs.current = [...optionRefs.current, { element: optionRef, value: optionValue }];
    },
    [value]
  );
  const unregisterOption = useCallback((optionRef) => {
    optionRefs.current = optionRefs.current.filter((ref) => ref.element !== optionRef);
  }, []);
  const updateIndicator = () => {
    var _a2, _b2;
    const containerRect = (_a2 = optionsRef.current) == null ? void 0 : _a2.getBoundingClientRect();
    const currentOptionRect = (_b2 = currentOption.current) == null ? void 0 : _b2.getBoundingClientRect();
    if (!containerRect || !currentOptionRect)
      return;
    setIndicator({ x: currentOptionRect.x - containerRect.x, width: currentOptionRect.width });
  };
  useLayoutEffect(() => {
    if (!currentOption.current)
      return;
    const resizeObserver = new ResizeObserver(updateIndicator);
    resizeObserver.observe(currentOption.current);
    updateIndicator();
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  useLayoutEffect(() => {
    var _a2;
    setTransitioning(true);
    currentOption.current = (_a2 = optionRefs.current.find((item) => item.value === value)) == null ? void 0 : _a2.element.current;
    updateIndicator();
  }, [value]);
  return /* @__PURE__ */ React.createElement(
    SegmentedControlContext.Provider,
    {
      value: { optionRefs, currentValue: value, onChange, registerOption, unregisterOption, size }
    },
    /* @__PURE__ */ React.createElement(
      "div",
      __spreadValues({
        className: classNames(styles.container, className),
        role: "radiogroup",
        tabIndex: 0,
        "aria-labelledby": labelId,
        onKeyDown: handleKeyDown,
        "aria-invalid": !!error,
        "aria-describedby": !!error ? errorId : void 0
      }, props),
      /* @__PURE__ */ React.createElement(Label, { long: true, element: "label", hidden: labelHidden, id: labelId, size: labelSize.get(size) || "m" }, label),
      /* @__PURE__ */ React.createElement("div", { className: styles.options({ size, asymmetric }), ref: optionsRef }, !!indicator && /* @__PURE__ */ React.createElement(
        "div",
        {
          ref: indicatorRef,
          className: styles.indicator({ transitioning }),
          onTransitionEnd: () => setTransitioning(false),
          style: assignInlineVars({
            [styles.indicatorLeft]: `${indicator.x}px`,
            [styles.indicatorWidth]: `${indicator.width}px`
          })
        }
      ), children),
      /* @__PURE__ */ React.createElement(Expandable, { expanded: !!error, timeout: duration.s }, ({ expandableElementRef }) => /* @__PURE__ */ React.createElement("div", { id: errorId, className: styles.error, ref: expandableElementRef, "aria-live": "polite" }, /* @__PURE__ */ React.createElement("div", { className: styles.errorIcon }, /* @__PURE__ */ React.createElement(Warning, { size: IconSize.Small, color: "critical" })), /* @__PURE__ */ React.createElement(Text, { lineHeight: "compact", size: "s" }, error)))
    )
  );
};
const optionTextSize = /* @__PURE__ */ new Map([
  ["xs", "xs"],
  ["s", "s"],
  ["m", "s"],
  ["l", "m"]
]);
export const SegmentedControlOption = (_c) => {
  var _d = _c, {
    value,
    icon,
    startElement,
    endElement,
    labelHidden,
    label,
    disabled,
    tooltip,
    className
  } = _d, props = __objRest(_d, [
    "value",
    "icon",
    "startElement",
    "endElement",
    "labelHidden",
    "label",
    "disabled",
    "tooltip",
    "className"
  ]);
  var _a, _b, _c2;
  const { optionRefs, currentValue, onChange, registerOption, unregisterOption, size } = useContext(SegmentedControlContext);
  const optionRef = useRef(null);
  const isSelected = currentValue === value;
  const currentIndex = (_a = optionRefs == null ? void 0 : optionRefs.current) == null ? void 0 : _a.indexOf(
    optionRefs.current.find((option) => option.value === value) || optionRefs.current[0]
  );
  const selectedIndex = ((_b = optionRefs == null ? void 0 : optionRefs.current) == null ? void 0 : _b.indexOf(
    optionRefs.current.find((option) => option.value === currentValue) || optionRefs.current[0]
  )) || 0;
  const isLast = currentIndex === (((_c2 = optionRefs.current) == null ? void 0 : _c2.length) || 0) - 1;
  const hasSeparator = !isSelected && currentIndex !== selectedIndex - 1 && !isLast;
  useLayoutEffect(() => {
    registerOption(optionRef, value);
    return () => {
      unregisterOption(optionRef, value);
    };
  }, [registerOption, unregisterOption, value]);
  const renderButton = () => /* @__PURE__ */ React.createElement(
    "button",
    __spreadValues({
      className: classNames(styles.button({ separator: hasSeparator }), className),
      tabIndex: isSelected ? 0 : -1,
      role: "radio",
      type: "button",
      "aria-checked": isSelected,
      "aria-label": labelHidden && typeof label === "string" ? label : void 0,
      onClick: () => onChange(value),
      ref: optionRef,
      disabled
    }, props),
    !!startElement && /* @__PURE__ */ React.createElement("div", { className: styles.element }, startElement),
    !!icon && /* @__PURE__ */ React.createElement("div", { className: styles.element }, icon),
    !labelHidden && /* @__PURE__ */ React.createElement(Text, { className: styles.text, strong: true, truncate: true, size: optionTextSize.get(size) || "s", hidden: labelHidden }, label),
    !!endElement && /* @__PURE__ */ React.createElement("div", { className: styles.element }, endElement),
    /* @__PURE__ */ React.createElement(Separator, { direction: "vertical", size: "65%", className: styles.separator({ visible: hasSeparator }) })
  );
  if (!!tooltip) {
    return /* @__PURE__ */ React.createElement(Tooltip, __spreadValues({}, tooltip), renderButton());
  }
  return renderButton();
};
